import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { X, Download, RefreshCw, Camera, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import { exportToExcel } from "Common/Components/Excel";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import axios from "axios";
import ImageLightbox from "Common/Components/ImageLightbox";
import { getRoleId } from "Common/Components/getRoleId";
import GPSLocation from "Common/Components/GPSLocation";
import { useNavigate } from "react-router-dom";
import LocationModal, { hasValidLocation } from "Common/Components/LocationModal";
import Swal from "sweetalert2";
import InspectorTaskColumns from "./DataColumn";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== Types ==========
interface OrderImage {
    created_at: string; created_at_jalali: string; id: number;
    category: string; url: string; file_name: string; file_size: number;
}

interface InspectorTask {
    id: number;
    created_at: string; created_at_jalali: string;
    updated_at: string; updated_at_jalali: string;
    baker_name: string; baker_phone: string;
    baker_code: number;
    driver_name: string; driver_national_id: string;
    inspector_counted_count: number | null;
    /**
     * موقعیت ثبت‌شده در پروفایل خبازی (نه موقعیت راننده هنگام تخلیه).
     * نام فیلد در API تغییر نکرده ولی معنای آن عوض شده است.
     * null بودن هر دو یعنی موقعیت ثبت نشده — نباید به مقدار جایگزین fallback شود.
     */
    driver_location_lat?: number | null;
    driver_location_lng?: number | null;
    inspector_location_lat?: number | null;
    inspector_location_lng?: number | null;
    images: OrderImage[];
    can_submit_inspector_submission: boolean;
    discharge_date_jalali?: string;
    inspection_date_jalali?: string;
    city?: string;
    province?: string;
    baker_address?: string;
    /** بازرس ثبت‌کننده رکورد؛ برای رکوردهای قبل از افزوده‌شدن این فیلد null است */
    inspector_id?: number | null;
    inspector_name?: string | null;
}

interface TasksApiResponse {
    total: number; size: number; pages: number; items: InspectorTask[];
}

// ========== CameraCapture ==========
interface CameraCaptureProps {
    maxCount: number;
    onChange: React.Dispatch<React.SetStateAction<File[]>>;
    currentFiles: File[];
    buttonLabel: string;
    buttonClassName?: string;
}
const CameraCapture: React.FC<CameraCaptureProps> = ({
    maxCount,
    onChange,
    currentFiles,
    buttonLabel,
    buttonClassName = "",
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onChange((prev) => {
                if (prev.length < maxCount) {
                    return [...prev, file];
                }
                return prev;
            });
        }
        if (inputRef.current) inputRef.current.value = "";
    };

    const removeImage = (index: number) => {
        onChange((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <Camera size={18} />
                <span className="text-sm text-gray-500">
                    حداکثر <span className="font-bold text-blue-600 underline">{maxCount}</span> عکس
                </span>
            </div>
            <input
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                ref={inputRef}
                onChange={handleCapture}
            />
            <button
                type="button"
                disabled={currentFiles.length >= maxCount}
                onClick={() => inputRef.current?.click()}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 ${buttonClassName}`}
            >
                {buttonLabel}
            </button>
            {currentFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                    {currentFiles.map((file, idx) => (
                        <div key={idx} className="relative border rounded overflow-hidden">
                            <img
                                src={URL.createObjectURL(file)}
                                alt={`preview-${idx}`}
                                className="w-full h-24 object-cover"
                            />
                            <button
                                onClick={() => removeImage(idx)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ========== کامپوننت اصلی ==========
const InspectorTasks = () => {
    const [activeTab, setActiveTab] = useState<"not_done" | "done">("not_done");
    const [tasks, setTasks] = useState<InspectorTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);
    const [dischargeStartDate, setDischargeStartDate] = useState<string>("");
    const [dischargeEndDate, setDischargeEndDate] = useState<string>("");

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("inspector-tasks", (filter) => {
        if (filter.status) setActiveTab(filter.status);
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        setSearchTerm(filter.search || "");
        setCurrentPage(1);
    });

    // ===== Stateهای مرتب‌سازی =====
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // ===== سایر state‌ها =====
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState<InspectorTask | null>(null);
    const navigate = useNavigate();
    const [inspectorLat, setInspectorLat] = useState<number | null>(null);
    const [inspectorLng, setInspectorLng] = useState<number | null>(null);
    const [inspectorCount, setInspectorCount] = useState<string>("");
    const [inspectorImages, setInspectorImages] = useState<File[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const [showLocationModal, setShowLocationModal] = useState(false);
    const [locationTask, setLocationTask] = useState<InspectorTask | null>(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [galleryImages, setGalleryImages] = useState<OrderImage[]>([]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // ===== تابع ترجمه خطاها =====
    const translateErrorMessage = (message: string): string => {
        if (!message) return "خطای ناشناخته";
        const errorMap: { [key: string]: string } = {
            "Cannot delete driver submission while inspector submission exists":
                "امکان حذف تسک راننده وجود ندارد زیرا تسک بازرس هنوز موجود است. ابتدا تسک بازرس را حذف کنید.",
            "You don't have permission": "شما دسترسی لازم برای این عملیات را ندارید.",
            "Task not found": "تسک مورد نظر یافت نشد.",
            "Invalid request": "درخواست نامعتبر است.",
            "Internal server error": "خطای داخلی سرور. لطفاً بعداً تلاش کنید.",
            "Unauthorized": "احراز هویت ناموفق. لطفاً مجدداً وارد شوید.",
            "Forbidden": "شما دسترسی به این عملیات را ندارید.",
        };
        for (const [en, fa] of Object.entries(errorMap)) {
            if (message.includes(en)) return fa;
        }
        return message;
    };

    const Token = localStorage.getItem("app_havir");
    const userRole = getRoleId(Token);

    // ===== دریافت لیست صفحه‌بندی شده =====
    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {
                page: currentPage,
                size: selectedNumber,
                status: activeTab,
            };
            if (searchTerm.trim()) params.search = searchTerm.trim();
            if (dischargeStartDate) params.discharge_start_date = dischargeStartDate;
            if (dischargeEndDate) params.discharge_end_date = dischargeEndDate;
            if (sortBy) {
                params.sort_by = sortBy;
                params.sort_order = sortOrder;
            }

            const res = await axios.get<TasksApiResponse>(
                `https://rasad-api.havirard.ir/monitoring/tasks/inspector`,
                { params, headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
            );
            setTasks(res.data.items || []);
            setTotal(res.data.total || 0);
            setTotalPages(res.data.pages || 0);
        } catch (err: any) {
            if (err.response?.status === 401) {
                localStorage.clear();
                navigate("/login");
            } else {
                toast.error("خطا در دریافت لیست وظایف");
            }
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedNumber, searchTerm, activeTab, navigate, dischargeStartDate, dischargeEndDate, sortBy, sortOrder]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    // ===== جلوگیری از overflow body =====
    useEffect(() => {
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';
        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    // ===== توابع مدیریت مرتب‌سازی =====
    const handleSort = useCallback((field: string) => {
        let sortField = field;
        if (field === 'discharge_date_jalali') sortField = 'discharge_date';
        else if (field === 'inspection_date_jalali') sortField = 'inspection_date';
        else return;

        if (sortBy === sortField) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(sortField);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    }, [sortBy]);

    const renderSortableHeader = (label: string, field: string) => {
        const sortField = field === "discharge_date_jalali" ? "discharge_date" : "inspection_date";
        const isActive = sortBy === sortField;
        return (
            <div className="flex items-center gap-1 cursor-pointer select-none" onClick={() => handleSort(field)}>
                {label}
                {isActive ? (
                    sortOrder === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                ) : (
                    <ArrowUpDown size={16} className="text-gray-400" />
                )}
            </div>
        );
    };

    // ===== دریافت همه‌ی داده‌ها برای اکسل =====
    const fetchAllTasks = async (status: "not_done" | "done", search?: string) => {
        let allItems: InspectorTask[] = [];
        let page = 1;
        const size = 100;
        let hasMore = true;

        while (hasMore) {
            try {
                const res = await axios.get<TasksApiResponse>(
                    `https://rasad-api.havirard.ir/monitoring/tasks/inspector`,
                    {
                        params: {
                            page,
                            size,
                            status,
                            ...(search?.trim() && { search: search.trim() }),
                            ...(sortBy && { sort_by: sortBy, sort_order: sortOrder }),
                            _t: Date.now(),
                        },
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }
                );
                const items = res.data.items || [];
                allItems = [...allItems, ...items];
                if (items.length < size) {
                    hasMore = false;
                } else {
                    page++;
                }
            } catch (error) {
                console.error("خطا در دریافت صفحه", page, error);
                hasMore = false;
                throw error;
            }
        }
        return allItems;
    };

    // ===== خروجی اکسل =====
    const handleExportExcel = async () => {
        try {
            Swal.fire({
                title: "در حال دریافت اطلاعات",
                text: "در حال دریافت تمام داده‌ها...",
                icon: "info",
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const allTasks = await fetchAllTasks(activeTab, searchTerm || undefined);

            if (allTasks.length === 0) {
                Swal.close();
                toast.warning("داده‌ای برای صادرات وجود ندارد");
                return;
            }

            const exportData = allTasks.map((t) => ({
                "شناسه": t.id,
                "تاریخ ثبت راننده": t.discharge_date_jalali || "-",
                "تاریخ ثبت بازرس": t.inspection_date_jalali || "-",
                "شهرستان": t.city || "نامشخص",
                "نام نانوا": t.baker_name || "-",
                "کد نانوا": t.baker_code || "-",
                "شماره تماس نانوا": t.baker_phone || "-",
                "تعداد کیسه بازرسی": t.inspector_counted_count !== null ? t.inspector_counted_count : "ثبت نشده",
                ...(activeTab === "done" ? { "نام بازرس": t.inspector_name || "ثبت نشده" } : {}),
                "تعداد تصاویر": t.images?.length ?? 0,
                "موقعیت مکانی نانوا": hasValidLocation(t.driver_location_lat, t.driver_location_lng)
                    ? `${t.driver_location_lat} , ${t.driver_location_lng}`
                    : "ثبت نشده",
                "موقعیت مکانی بازرس": hasValidLocation(t.inspector_location_lat, t.inspector_location_lng)
                    ? `${t.inspector_location_lat} , ${t.inspector_location_lng}`
                    : "ثبت نشده",
            }));

            exportToExcel(exportData, `InspectorTasks-${activeTab}-full`);
            toast.success(`${exportData.length} رکورد با موفقیت صادر شد`);
            Swal.close();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || "خطا در دریافت داده‌ها";
            toast.error(translateErrorMessage(errorMsg));
            Swal.close();
        }
    };

    // ===== توابع مربوط به مودال‌ها و عملیات =====
    const openSubmitModal = (task: InspectorTask) => {
        setSelectedTask(task);
        setInspectorLat(null);
        setInspectorLng(null);
        const count = task.inspector_counted_count;
        setInspectorCount(count != null && count > 0 ? String(count) : "");
        setInspectorImages([]);
        setShowSubmitModal(true);
    };

    const closeSubmitModal = () => {
        setShowSubmitModal(false);
        setSelectedTask(null);
    };

    const openLocationModal = (task: InspectorTask) => {
        setLocationTask(task);
        setShowLocationModal(true);
    };

    const closeLocationModal = () => {
        setShowLocationModal(false);
        setLocationTask(null);
    };

    const openGallery = (images: OrderImage[]) => {
        setGalleryImages(images || []);
        setShowGalleryModal(true);
    };

    const openDELETEModal = async (Data: InspectorTask) => {
        const result = await Swal.fire({
            title: 'آیا مطمئن هستید؟',
            text: 'این عملیات قابل بازگشت نیست!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'بله، تأیید می‌کنم',
            cancelButtonText: 'لغو'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("app_havir");
                const response = await axios.delete(
                    `https://rasad-api.havirard.ir/monitoring/tasks/inspector/${Data.id}/submission`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                if (response.status === 200) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'عملیات با موفقیت انجام شد',
                        text: 'تسک مورد نظر حذف گردید.'
                    });
                    fetchTasks();
                }
            } catch (error: any) {
                let errorMessage = 'خطایی رخ داده است. لطفاً مجدداً تلاش کنید.';
                if (axios.isAxiosError(error)) {
                    const status = error.response?.status;
                    let serverMessage =
                        error.response?.data?.detail ||
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        "";
                    if (serverMessage) {
                        errorMessage = translateErrorMessage(serverMessage);
                    } else {
                        if (status === 404) errorMessage = 'تسک مورد نظر یافت نشد.';
                        else if (status === 403) errorMessage = 'شما دسترسی لازم برای این عملیات را ندارید.';
                        else if (status === 400) errorMessage = 'درخواست نامعتبر است.';
                        else if (status === 409) errorMessage = 'تضاد در داده‌ها. لطفاً ابتدا وابستگی‌ها را حذف کنید.';
                        else if (status === 500) errorMessage = 'خطای داخلی سرور. لطفاً بعداً تلاش کنید.';
                        else if (status === 401) errorMessage = 'احراز هویت ناموفق. لطفاً مجدداً وارد شوید.';
                    }
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }
                await Swal.fire({
                    icon: 'error',
                    title: 'خطا',
                    text: errorMessage,
                    confirmButtonColor: '#d33'
                });
            }
        }
    };

    const uploadInspectorImages = async (orderId: number, files: File[]) => {
        if (files.length === 0) return;
        const formData = new FormData();
        files.forEach(file => formData.append("files", file));
        await axios.post(
            `https://rasad-api.havirard.ir/monitoring/orders/${orderId}/images/inspector`,
            formData,
            { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
        );
    };

    const handleSubmitTask = async () => {
        if (!selectedTask) return;
        if (inspectorImages.length > 5) {
            toast.error("حداکثر ۵ تصویر مجاز است");
            return;
        }
        setSubmitting(true);
        try {
            // ۱- آپلود عکس
            if (inspectorImages.length > 0) {
                await uploadInspectorImages(selectedTask.id, inspectorImages);
            }
            // ۲- ثبت اطلاعات
            await axios.put(
                `https://rasad-api.havirard.ir/monitoring/tasks/inspector/${selectedTask.id}/submission`,
                {
                    inspector_location_lat: inspectorLat || 0,
                    inspector_location_lng: inspectorLng || 0,
                    inspector_counted_count: Number(inspectorCount) || 0,
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
            );
            toast.success("اطلاعات بازرسی با موفقیت ثبت شد");
            closeSubmitModal();
            fetchTasks();
        } catch (err: any) {
            if (err.response?.status === 403) {
                const message = err.response?.data?.message || "";
                if (message.includes("جدیدتر") || message.includes("قدیمی") || message.includes("دیگر قابل ثبت نیست")) {
                    toast.error("این رکورد به دلیل ثبت تخلیه جدیدتر برای این نانوا دیگر قابل ثبت نیست. لطفا لیست را به‌روزرسانی کنید.");
                    fetchTasks();
                } else {
                    toast.error(message || "خطا در ثبت اطلاعات");
                }
            } else {
                toast.error(err.response?.data?.message || "خطا در ثبت اطلاعات");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleSizeChange = (val: number) => { setSelectedNumber(val); setCurrentPage(1); };
    const handleSearchChange = (val: string) => { setSearchTerm(val); setCurrentPage(1); };
    const handleTabChange = (tab: "not_done" | "done") => { setActiveTab(tab); setCurrentPage(1); };

    // ===== تعریف ستون‌ها =====
    const columns = useMemo(() => {
        return InspectorTaskColumns({
            userRole: userRole ?? 10,
            activeTab,
            openSubmitModal,
            openGallery,
            openLocationModal,
            openDELETEModal,
            renderSortableHeader,
        });
    }, [userRole, activeTab, openSubmitModal, openGallery, openLocationModal, openDELETEModal, renderSortableHeader]);

    // ===== رندر =====
    return (
        <React.Fragment>
            <BreadCrumb pageTitle="وظایف بازرس شهرستان" title="پایش آرد" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

            <div className="card">
                <div className="card-body">
                    <div className="flex gap-4 mb-4 border-b dark:border-gray-700">
                        <button
                            onClick={() => handleTabChange("not_done")}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "not_done" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}
                        >
                            ثبت‌نشده‌ها
                        </button>
                        <button
                            onClick={() => handleTabChange("done")}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "done" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}
                        >
                            ثبت‌شده‌ها
                        </button>
                    </div>

                    <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-2">
                            <RefreshCw className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`} onClick={fetchTasks} />
                            <h6 className="text-lg font-semibold">مجموع وظایف <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">{total}</span></h6>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <TableDropDownSmall onSelect={handleSizeChange} />
                            <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
                                <Download size={16} /> اکسل
                            </button>
                        </div>
                    </div>

                    <div className="relative mb-2">
                        <input
                            type="text"
                            placeholder="جستجو..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                        />
                        {searchTerm && (
                            <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                                <X className="size-4" />
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : tasks.length === 0 ? (
                        <LoadingCounter count={0} text="وظیفه‌ای یافت نشد" />
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={tasks}
                                customPageSize={selectedNumber}
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            <PaginationTest
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* مودال نمایش موقعیت مکانی روی نقشه */}
            <LocationModal
                show={showLocationModal}
                onHide={closeLocationModal}
                lat={locationTask?.driver_location_lat}
                lng={locationTask?.driver_location_lng}
                title={`موقعیت مکانی نانوایی${locationTask?.baker_name ? ` - ${locationTask.baker_name}` : ""}`}
                registeredAddress={locationTask?.baker_address}
                info={[
                    { label: "نانوا", value: locationTask?.baker_name, icon: "user" },
                    { label: "کد نانوا", value: locationTask?.baker_code, icon: "home" },
                    { label: "شماره تماس", value: locationTask?.baker_phone, icon: "phone" },
                    {
                        label: "شهرستان",
                        value: [locationTask?.province, locationTask?.city].filter(Boolean).join(" / "),
                        icon: "city",
                    },
                    { label: "تاریخ ثبت راننده", value: locationTask?.discharge_date_jalali, icon: "date" },
                    { label: "تاریخ ثبت بازرس", value: locationTask?.inspection_date_jalali, icon: "date" },
                ]}
            />

            {/* مودال گالری */}
            <Modal
                show={showGalleryModal}
                onHide={() => setShowGalleryModal(false)}
                modal-center="true"
                className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
                dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
                    <Modal.Title className="text-lg text-white font-bold">گالری تصاویر</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    {galleryImages.length === 0 ? (
                        <p className="text-center text-white/50">تصویری وجود ندارد</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            {galleryImages.map((img, idx) => {
                                const categoryLabel = img.category === "pre_unloading" ? "قبل از تخلیه" : img.category === "post_unloading" ? "بعد از تخلیه" : "بازرس";
                                return (
                                    <div
                                        key={img.id}
                                        className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                                        onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                                    >
                                        <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                        <div className="p-2 text-xs bg-white/5">{categoryLabel} | {img.file_name}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end p-5 border-t border-white/10">
                    <button onClick={() => setShowGalleryModal(false)} className="px-4 py-2 text-sm bg-white/10 rounded-lg hover:bg-white/20">بستن</button>
                </Modal.Footer>
            </Modal>

            {/* مودال ثبت بازرسی */}
            <Modal
                show={showSubmitModal}
                onHide={closeSubmitModal}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[45rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b">
                    <Modal.Title className="text-lg font-bold">ثبت بازرسی #{selectedTask?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto space-y-6">
                    <GPSLocation
                        onLocation={(lat, lng) => { setInspectorLat(lat); setInspectorLng(lng); }}
                        lat={inspectorLat}
                        lng={inspectorLng}
                    />
                    <div>
                        <label className="block mb-1 text-sm font-medium">تعداد شمارش شده بازرس</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={inspectorCount}
                            onChange={(e) => setInspectorCount(e.target.value)}
                            placeholder="تعداد را وارد کنید"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700"
                        />
                    </div>
                    <div>
                        <CameraCapture
                            maxCount={5}
                            onChange={setInspectorImages}
                            currentFiles={inspectorImages}
                            buttonLabel="تصاویر بازرس"
                            buttonClassName="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg"
                        />
                    </div>
                    <div className="p-3 bg-yellow-50 text-yellow-800 rounded-lg text-xs dark:bg-yellow-900/20 dark:text-yellow-300">
                        <strong>توجه:</strong> موقعیت مکانی به‌صورت خودکار از GPS دستگاه دریافت می‌شود. در صورت بروز خطا، دوباره تلاش کنید.
                    </div>
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3 p-5 border-t">
                    <button onClick={closeSubmitModal} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">انصراف</button>
                    <button onClick={handleSubmitTask} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
                        {submitting ? "در حال ثبت..." : "ثبت بازرسی"}
                    </button>
                </Modal.Footer>
            </Modal>

            {lightboxOpen && (
                <ImageLightbox
                    images={galleryImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </React.Fragment>
    );
};

export default InspectorTasks;
// import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
// import BreadCrumb from "Common/BreadCrumb";
// import { X, Download, RefreshCw, Camera, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
// import TableContainer from "Common/TableContainer";
// import Modal from "Common/Components/Modal";
// import { toast, ToastContainer } from "react-toastify";
// import TableDropDownSmall from "pages/Components/TableDropDownSmall";
// import { exportToExcel } from "Common/Components/Excel";
// import LoadingCounter from "Common/Components/LoadingCounterProps";
// import PaginationTest from "Common/Components/Paginationtest";
// import axios from "axios";
// import ImageLightbox from "Common/Components/ImageLightbox";
// import { getRoleId } from "Common/Components/getRoleId";
// import GPSLocation from "Common/Components/GPSLocation";
// import { useNavigate } from "react-router-dom";
// import StaticMapMarker from "Common/Components/StaticArcMap";
// import Swal from "sweetalert2";
// import InspectorTaskColumns from "./DataColumn";

// // ========== Types ==========
// interface OrderImage {
//     created_at: string; created_at_jalali: string; id: number;
//     category: string; url: string; file_name: string; file_size: number;
// }

// interface InspectorTask {
//     id: number;
//     created_at: string; created_at_jalali: string;
//     updated_at: string; updated_at_jalali: string;
//     baker_name: string; baker_phone: string;
//     baker_code: number;   // یا baker_code ?: number; اگر ممکن است null باشد
//     driver_name: string; driver_national_id: string;
//     inspector_counted_count: number | null;
//     driver_location_lat?: number | null;
//     driver_location_lng?: number | null;
//     images: OrderImage[];
//     can_submit_inspector_submission: boolean;
//     discharge_date_jalali?: string;
//     inspection_date_jalali?: string;
//     city?: string;
// }

// interface TasksApiResponse {
//     total: number; size: number; pages: number; items: InspectorTask[];
// }

// // ========== CameraCapture ==========
// interface CameraCaptureProps {
//     maxCount: number;
//     onChange: React.Dispatch<React.SetStateAction<File[]>>;
//     currentFiles: File[];
//     buttonLabel: string;
//     buttonClassName?: string;
// }
// const CameraCapture: React.FC<CameraCaptureProps> = ({
//     maxCount,
//     onChange,
//     currentFiles,
//     buttonLabel,
//     buttonClassName = "",
// }) => {
//     const inputRef = useRef<HTMLInputElement>(null);

//     const handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
//         const file = e.target.files?.[0];
//         if (file) {
//             onChange((prev) => {
//                 if (prev.length < maxCount) {
//                     return [...prev, file];
//                 }
//                 return prev;
//             });
//         }
//         if (inputRef.current) inputRef.current.value = "";
//     };

//     const removeImage = (index: number) => {
//         onChange((prev) => prev.filter((_, i) => i !== index));
//     };

//     return (
//         <div>
//             <div className="flex items-center gap-2 mb-2">
//                 <Camera size={18} />
//                 <span className="text-sm text-gray-500">
//                     حداکثر <span className="font-bold text-blue-600 underline">{maxCount}</span> عکس
//                 </span>
//             </div>
//             <input
//                 type="file"
//                 accept="image/*"
//                 capture="environment"
//                 style={{ display: "none" }}
//                 ref={inputRef}
//                 onChange={handleCapture}
//             />
//             <button
//                 type="button"
//                 disabled={currentFiles.length >= maxCount}
//                 onClick={() => inputRef.current?.click()}
//                 className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 ${buttonClassName}`}
//             >
//                 {buttonLabel}
//             </button>
//             {currentFiles.length > 0 && (
//                 <div className="mt-3 grid grid-cols-3 gap-2">
//                     {currentFiles.map((file, idx) => (
//                         <div key={idx} className="relative border rounded overflow-hidden">
//                             <img
//                                 src={URL.createObjectURL(file)}
//                                 alt={`preview-${idx}`}
//                                 className="w-full h-24 object-cover"
//                             />
//                             <button
//                                 onClick={() => removeImage(idx)}
//                                 className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
//                             >
//                                 <X size={12} />
//                             </button>
//                         </div>
//                     ))}
//                 </div>
//             )}
//         </div>
//     );
// };

// // ========== کامپوننت اصلی ==========
// const InspectorTasks = () => {
//     const [activeTab, setActiveTab] = useState<"not_done" | "done">("not_done");
//     const [tasks, setTasks] = useState<InspectorTask[]>([]);
//     const [loading, setLoading] = useState(false);
//     const [total, setTotal] = useState(0);
//     const [totalPages, setTotalPages] = useState(0);
//     const [searchTerm, setSearchTerm] = useState("");
//     const [currentPage, setCurrentPage] = useState(1);
//     const [selectedNumber, setSelectedNumber] = useState<number>(10);
//     const [dischargeStartDate, setDischargeStartDate] = useState<string>("");
//     const [dischargeEndDate, setDischargeEndDate] = useState<string>("");

//     // ===== Stateهای مرتب‌سازی =====
//     const [sortBy, setSortBy] = useState<string | null>(null);
//     const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

//     // ===== سایر state‌ها =====
//     const [showSubmitModal, setShowSubmitModal] = useState(false);
//     const [selectedTask, setSelectedTask] = useState<InspectorTask | null>(null);
//     const navigate = useNavigate();
//     const [inspectorLat, setInspectorLat] = useState<number | null>(null);
//     const [inspectorLng, setInspectorLng] = useState<number | null>(null);
//     const [inspectorCount, setInspectorCount] = useState<string>("");
//     const [inspectorImages, setInspectorImages] = useState<File[]>([]);
//     const [submitting, setSubmitting] = useState(false);

//     const [showDetailModal, setShowDetailModal] = useState(false);
//     const [detailTask, setDetailTask] = useState<InspectorTask | null>(null);
//     const [showGalleryModal, setShowGalleryModal] = useState(false);
//     const [galleryImages, setGalleryImages] = useState<OrderImage[]>([]);
//     const [lightboxOpen, setLightboxOpen] = useState(false);
//     const [lightboxIndex, setLightboxIndex] = useState(0);

//     // ===== تابع ترجمه خطاها =====
//     const translateErrorMessage = (message: string): string => {
//         if (!message) return "خطای ناشناخته";
//         const errorMap: { [key: string]: string } = {
//             "Cannot delete driver submission while inspector submission exists":
//                 "امکان حذف تسک راننده وجود ندارد زیرا تسک بازرس هنوز موجود است. ابتدا تسک بازرس را حذف کنید.",
//             "You don't have permission": "شما دسترسی لازم برای این عملیات را ندارید.",
//             "Task not found": "تسک مورد نظر یافت نشد.",
//             "Invalid request": "درخواست نامعتبر است.",
//             "Internal server error": "خطای داخلی سرور. لطفاً بعداً تلاش کنید.",
//             "Unauthorized": "احراز هویت ناموفق. لطفاً مجدداً وارد شوید.",
//             "Forbidden": "شما دسترسی به این عملیات را ندارید.",
//         };
//         for (const [en, fa] of Object.entries(errorMap)) {
//             if (message.includes(en)) return fa;
//         }
//         return message;
//     };

//     const Token = localStorage.getItem("app_havir");
//     const userRole = getRoleId(Token);

//     // ===== دریافت لیست صفحه‌بندی شده =====
//     const fetchTasks = useCallback(async () => {
//         setLoading(true);
//         try {
//             const params: any = {
//                 page: currentPage,
//                 size: selectedNumber,
//                 status: activeTab,
//             };
//             if (searchTerm.trim()) params.search = searchTerm.trim();
//             if (dischargeStartDate) params.discharge_start_date = dischargeStartDate;
//             if (dischargeEndDate) params.discharge_end_date = dischargeEndDate;
//             if (sortBy) {
//                 params.sort_by = sortBy;
//                 params.sort_order = sortOrder;
//             }

//             const res = await axios.get<TasksApiResponse>(
//                 `https://rasad-api.havirard.ir/monitoring/tasks/inspector`,
//                 { params, headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
//             );
//             setTasks(res.data.items || []);
//             setTotal(res.data.total || 0);
//             setTotalPages(res.data.pages || 0);
//         } catch (err: any) {
//             if (err.response?.status === 401) {
//                 localStorage.clear();
//                 navigate("/login");
//             } else {
//                 toast.error("خطا در دریافت لیست وظایف");
//             }
//             setTasks([]);
//         } finally {
//             setLoading(false);
//         }
//     }, [currentPage, selectedNumber, searchTerm, activeTab, navigate, dischargeStartDate, dischargeEndDate, sortBy, sortOrder]);

//     useEffect(() => {
//         fetchTasks();
//     }, [fetchTasks]);

//     // ===== جلوگیری از overflow body =====
//     useEffect(() => {
//         document.body.classList.remove('overflow-hidden');
//         document.body.style.overflow = '';
//         const observer = new MutationObserver(() => {
//             if (document.body.classList.contains('overflow-hidden')) {
//                 document.body.classList.remove('overflow-hidden');
//                 document.body.style.overflow = '';
//             }
//         });
//         observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
//         return () => observer.disconnect();
//     }, []);

//     // ===== توابع مدیریت مرتب‌سازی =====
//     const handleSort = useCallback((field: string) => {
//         let sortField = field;
//         if (field === 'discharge_date_jalali') sortField = 'discharge_date';
//         else if (field === 'inspection_date_jalali') sortField = 'inspection_date';
//         else return;

//         if (sortBy === sortField) {
//             setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
//         } else {
//             setSortBy(sortField);
//             setSortOrder('asc');
//         }
//         setCurrentPage(1);
//     }, [sortBy]);

//     const renderSortableHeader = (label: string, field: string) => {
//         const sortField = field === "discharge_date_jalali" ? "discharge_date" : "inspection_date";
//         const isActive = sortBy === sortField;
//         return (
//             <div className="flex items-center gap-1 cursor-pointer select-none" onClick={() => handleSort(field)}>
//                 {label}
//                 {isActive ? (
//                     sortOrder === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />
//                 ) : (
//                     <ArrowUpDown size={16} className="text-gray-400" />
//                 )}
//             </div>
//         );
//     };

//     // ===== دریافت همه‌ی داده‌ها برای اکسل =====
//     const fetchAllTasks = async (status: "not_done" | "done", search?: string) => {
//         let allItems: InspectorTask[] = [];
//         let page = 1;
//         const size = 100; // حداکثر مجاز طبق مستندات
//         let hasMore = true;

//         while (hasMore) {
//             try {
//                 const res = await axios.get<TasksApiResponse>(
//                     `https://rasad-api.havirard.ir/monitoring/tasks/inspector`,
//                     {
//                         params: {
//                             page,
//                             size,
//                             status,
//                             ...(search?.trim() && { search: search.trim() }),
//                             ...(sortBy && { sort_by: sortBy, sort_order: sortOrder }),
//                             _t: Date.now(),
//                         },
//                         headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
//                     }
//                 );
//                 const items = res.data.items || [];
//                 allItems = [...allItems, ...items];
//                 if (items.length < size) {
//                     hasMore = false;
//                 } else {
//                     page++;
//                 }
//             } catch (error) {
//                 console.error("خطا در دریافت صفحه", page, error);
//                 hasMore = false;
//                 throw error;
//             }
//         }
//         return allItems;
//     };

//     // ===== خروجی اکسل =====
//     const handleExportExcel = async () => {
//         try {
//             Swal.fire({
//                 title: "در حال دریافت اطلاعات",
//                 text: "در حال دریافت تمام داده‌ها...",
//                 icon: "info",
//                 allowOutsideClick: false,
//                 didOpen: () => Swal.showLoading(),
//             });

//             const allTasks = await fetchAllTasks(activeTab, searchTerm || undefined);

//             if (allTasks.length === 0) {
//                 Swal.close();
//                 toast.warning("داده‌ای برای صادرات وجود ندارد");
//                 return;
//             }

//             const exportData = allTasks.map((t) => ({
//                 "شناسه": t.id,
//                 "تاریخ ثبت راننده": t.discharge_date_jalali || "-",
//                 "تاریخ ثبت بازرس": t.inspection_date_jalali || "-",
//                 "شهرستان": t.city || "نامشخص",
//                 "نام نانوا": t.baker_name || "-",
//                 "کد نانوا": t.baker_code || "-",
//                 "شماره تماس نانوا": t.baker_phone || "-",
//                 "تعداد کیسه بازرسی": t.inspector_counted_count !== null ? t.inspector_counted_count : "ثبت نشده",
//                 "تعداد تصاویر": t.images?.length ?? 0,
//                 "موقعیت مکانی نانوا": (t.driver_location_lat && t.driver_location_lng)
//                     ? `${t.driver_location_lat} , ${t.driver_location_lng}`
//                     : "ثبت نشده",
//             }));

//             exportToExcel(exportData, `InspectorTasks-${activeTab}-full`);
//             toast.success(`${exportData.length} رکورد با موفقیت صادر شد`);
//             Swal.close();
//         } catch (error: any) {
//             const errorMsg = error.response?.data?.message || "خطا در دریافت داده‌ها";
//             toast.error(translateErrorMessage(errorMsg));
//             Swal.close();
//         }
//     };

//     // ===== توابع مربوط به مودال‌ها و عملیات =====
//     const openSubmitModal = (task: InspectorTask) => {
//         setSelectedTask(task);
//         setInspectorLat(null);
//         setInspectorLng(null);
//         const count = task.inspector_counted_count;
//         setInspectorCount(count != null && count > 0 ? String(count) : "");
//         setInspectorImages([]);
//         setShowSubmitModal(true);
//     };

//     const closeSubmitModal = () => {
//         setShowSubmitModal(false);
//         setSelectedTask(null);
//     };

//     const openDetailModal = (task: InspectorTask) => {
//         setDetailTask(task);
//         setShowDetailModal(true);
//     };

//     const openGallery = (images: OrderImage[]) => {
//         setGalleryImages(images || []);
//         setShowGalleryModal(true);
//     };

//     const openDELETEModal = async (Data: InspectorTask) => {
//         const result = await Swal.fire({
//             title: 'آیا مطمئن هستید؟',
//             text: 'این عملیات قابل بازگشت نیست!',
//             icon: 'warning',
//             showCancelButton: true,
//             confirmButtonColor: '#d33',
//             cancelButtonColor: '#3085d6',
//             confirmButtonText: 'بله، تأیید می‌کنم',
//             cancelButtonText: 'لغو'
//         });

//         if (result.isConfirmed) {
//             try {
//                 const token = localStorage.getItem("app_havir");
//                 const response = await axios.delete(
//                     `https://rasad-api.havirard.ir/monitoring/tasks/inspector/${Data.id}/submission`,
//                     {
//                         headers: {
//                             'Content-Type': 'application/json',
//                             'Authorization': `Bearer ${token}`
//                         }
//                     }
//                 );

//                 if (response.status === 200) {
//                     await Swal.fire({
//                         icon: 'success',
//                         title: 'عملیات با موفقیت انجام شد',
//                         text: 'تسک مورد نظر حذف گردید.'
//                     });
//                     fetchTasks();
//                 }
//             } catch (error: any) {
//                 let errorMessage = 'خطایی رخ داده است. لطفاً مجدداً تلاش کنید.';
//                 if (axios.isAxiosError(error)) {
//                     const status = error.response?.status;
//                     let serverMessage =
//                         error.response?.data?.detail ||
//                         error.response?.data?.message ||
//                         error.response?.data?.error ||
//                         "";
//                     if (serverMessage) {
//                         errorMessage = translateErrorMessage(serverMessage);
//                     } else {
//                         if (status === 404) errorMessage = 'تسک مورد نظر یافت نشد.';
//                         else if (status === 403) errorMessage = 'شما دسترسی لازم برای این عملیات را ندارید.';
//                         else if (status === 400) errorMessage = 'درخواست نامعتبر است.';
//                         else if (status === 409) errorMessage = 'تضاد در داده‌ها. لطفاً ابتدا وابستگی‌ها را حذف کنید.';
//                         else if (status === 500) errorMessage = 'خطای داخلی سرور. لطفاً بعداً تلاش کنید.';
//                         else if (status === 401) errorMessage = 'احراز هویت ناموفق. لطفاً مجدداً وارد شوید.';
//                     }
//                 } else if (error instanceof Error) {
//                     errorMessage = error.message;
//                 }
//                 await Swal.fire({
//                     icon: 'error',
//                     title: 'خطا',
//                     text: errorMessage,
//                     confirmButtonColor: '#d33'
//                 });
//             }
//         }
//     };

//     const uploadInspectorImages = async (orderId: number, files: File[]) => {
//         if (files.length === 0) return;
//         const formData = new FormData();
//         files.forEach(file => formData.append("files", file));
//         await axios.post(
//             `https://rasad-api.havirard.ir/monitoring/orders/${orderId}/images/inspector`,
//             formData,
//             { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
//         );
//     };

//     const handleSubmitTask = async () => {
//         if (!selectedTask) return;
//         if (inspectorImages.length > 5) {
//             toast.error("حداکثر ۵ تصویر مجاز است");
//             return;
//         }
//         setSubmitting(true);
//         try {
//             await axios.put(
//                 `https://rasad-api.havirard.ir/monitoring/tasks/inspector/${selectedTask.id}/submission`,
//                 {
//                     inspector_location_lat: inspectorLat || 0,
//                     inspector_location_lng: inspectorLng || 0,
//                     inspector_counted_count: Number(inspectorCount) || 0,
//                 },
//                 { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
//             );

//             if (inspectorImages.length > 0) {
//                 await uploadInspectorImages(selectedTask.id, inspectorImages);
//             }

//             toast.success("اطلاعات بازرسی با موفقیت ثبت شد");
//             closeSubmitModal();
//             fetchTasks();
//         } catch (err: any) {
//             if (err.response?.status === 403) {
//                 const message = err.response?.data?.message || "";
//                 if (message.includes("جدیدتر") || message.includes("قدیمی") || message.includes("دیگر قابل ثبت نیست")) {
//                     toast.error("این رکورد به دلیل ثبت تخلیه جدیدتر برای این نانوا دیگر قابل ثبت نیست. لطفا لیست را به‌روزرسانی کنید.");
//                     fetchTasks();
//                 } else {
//                     toast.error(message || "خطا در ثبت اطلاعات");
//                 }
//             } else {
//                 toast.error(err.response?.data?.message || "خطا در ثبت اطلاعات");
//             }
//         } finally {
//             setSubmitting(false);
//         }
//     };

//     const handlePageChange = (page: number) => setCurrentPage(page);
//     const handleSizeChange = (val: number) => { setSelectedNumber(val); setCurrentPage(1); };
//     const handleSearchChange = (val: string) => { setSearchTerm(val); setCurrentPage(1); };
//     const handleTabChange = (tab: "not_done" | "done") => { setActiveTab(tab); setCurrentPage(1); };

//     // ===== تعریف ستون‌ها (بعد از تعریف تمام توابع مورد نیاز) =====
//     const columns = useMemo(() => {
//         return InspectorTaskColumns({
//             userRole: userRole ?? 10,
//             openSubmitModal,
//             openGallery,
//             openDetailModal,
//             openDELETEModal,
//             renderSortableHeader,
//         });
//     }, [userRole, openSubmitModal, openGallery, openDetailModal, openDELETEModal, renderSortableHeader]);

//     // ===== رندر =====
//     return (
//         <React.Fragment>
//             <BreadCrumb pageTitle="وظایف بازرس شهرستان" title="پایش آرد" />
//             <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

//             <div className="card">
//                 <div className="card-body">
//                     <div className="flex gap-4 mb-4 border-b dark:border-gray-700">
//                         <button
//                             onClick={() => handleTabChange("not_done")}
//                             className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "not_done" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}
//                         >
//                             ثبت‌نشده‌ها
//                         </button>
//                         <button
//                             onClick={() => handleTabChange("done")}
//                             className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "done" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}
//                         >
//                             ثبت‌شده‌ها
//                         </button>
//                     </div>

//                     <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
//                         <div className="flex items-center gap-2">
//                             <RefreshCw className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`} onClick={fetchTasks} />
//                             <h6 className="text-lg font-semibold">مجموع وظایف <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">{total}</span></h6>
//                         </div>
//                         <div className="flex flex-wrap gap-3 items-center">
//                             <TableDropDownSmall onSelect={handleSizeChange} />
//                             <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
//                                 <Download size={16} /> اکسل
//                             </button>
//                         </div>
//                     </div>

//                     <div className="relative mb-2">
//                         <input
//                             type="text"
//                             placeholder="جستجو..."
//                             value={searchTerm}
//                             onChange={(e) => handleSearchChange(e.target.value)}
//                             className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
//                         />
//                         {searchTerm && (
//                             <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
//                                 <X className="size-4" />
//                             </button>
//                         )}
//                     </div>

//                     {loading ? (
//                         <LoadingCounter count={0} text="در حال بارگذاری..." />
//                     ) : tasks.length === 0 ? (
//                         <LoadingCounter count={0} text="وظیفه‌ای یافت نشد" />
//                     ) : (
//                         <>
//                             <TableContainer
//                                 isPagination={false}
//                                 columns={columns}
//                                 data={tasks}
//                                 customPageSize={selectedNumber}
//                                 divclassName="-mx-5 overflow-x-auto"
//                                 tableclassName="w-full whitespace-nowrap"
//                                 theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
//                                 thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
//                                 tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
//                                 PaginationClassName="hidden"
//                             />
//                             <PaginationTest
//                                 currentPage={currentPage}
//                                 totalPages={totalPages}
//                                 onPageChange={handlePageChange}
//                             />
//                         </>
//                     )}
//                 </div>
//             </div>

//             {/* مودال جزئیات */}
//             <Modal
//                 show={showDetailModal}
//                 onHide={() => setShowDetailModal(false)}
//                 modal-center="true"
//                 className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
//                 dialogClassName="w-screen md:w-[42rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
//             >
//                 <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
//                     <Modal.Title className="text-lg font-bold">جزئیات بازرسی #{detailTask?.id}</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
//                     {detailTask && (
//                         <div className="space-y-3 text-sm">
//                             <div><strong>نانوا:</strong> {detailTask.baker_name}</div>
//                             <div><strong>تلفن نانوا:</strong> {detailTask.baker_phone}</div>
//                             <div><strong>راننده:</strong> {detailTask.driver_name} ({detailTask.driver_national_id})</div>
//                             <div><strong>تعداد بازرسی:</strong> {detailTask.inspector_counted_count ?? "ثبت نشده"}</div>
//                             {detailTask.driver_location_lat != null && detailTask.driver_location_lng != null ? (
//                                 <StaticMapMarker
//                                     lat={detailTask.driver_location_lat}
//                                     lng={detailTask.driver_location_lng}
//                                     zoom={17}
//                                     width={800}
//                                     height={300}
//                                     apiKey="service.b30c3de70ec34167a3a9c8dabb96bd10"
//                                 />
//                             ) : (
//                                 <p className="text-gray-400">ثبت نشده</p>
//                             )}
//                             <div>
//                                 <strong>گالری تصاویر:</strong>{" "}
//                                 {detailTask.images?.length > 0 ? (
//                                     <button onClick={() => openGallery(detailTask.images)} className="text-blue-600 underline">
//                                         {detailTask.images.length} عکس
//                                     </button>
//                                 ) : "ندارد"}
//                             </div>
//                         </div>
//                     )}
//                 </Modal.Body>
//                 <Modal.Footer className="flex justify-end p-5 border-t dark:border-gray-700">
//                     <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">بستن</button>
//                 </Modal.Footer>
//             </Modal>

//             {/* مودال گالری */}
//             <Modal
//                 show={showGalleryModal}
//                 onHide={() => setShowGalleryModal(false)}
//                 modal-center="true"
//                 className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
//                 dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white"
//             >
//                 <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
//                     <Modal.Title className="text-lg text-white font-bold">گالری تصاویر</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
//                     {galleryImages.length === 0 ? (
//                         <p className="text-center text-white/50">تصویری وجود ندارد</p>
//                     ) : (
//                         <div className="grid grid-cols-2 gap-4">
//                             {galleryImages.map((img, idx) => {
//                                 const categoryLabel = img.category === "pre_unloading" ? "قبل از تخلیه" : img.category === "post_unloading" ? "بعد از تخلیه" : "بازرس";
//                                 return (
//                                     <div
//                                         key={img.id}
//                                         className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
//                                         onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
//                                     >
//                                         <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
//                                         <div className="p-2 text-xs bg-white/5">{categoryLabel} | {img.file_name}</div>
//                                     </div>
//                                 );
//                             })}
//                         </div>
//                     )}
//                 </Modal.Body>
//                 <Modal.Footer className="flex justify-end p-5 border-t border-white/10">
//                     <button onClick={() => setShowGalleryModal(false)} className="px-4 py-2 text-sm bg-white/10 rounded-lg hover:bg-white/20">بستن</button>
//                 </Modal.Footer>
//             </Modal>

//             {/* مودال ثبت بازرسی */}
//             <Modal
//                 show={showSubmitModal}
//                 onHide={closeSubmitModal}
//                 modal-center="true"
//                 className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
//                 dialogClassName="w-screen md:w-[45rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
//             >
//                 <Modal.Header className="flex items-center justify-between p-5 border-b">
//                     <Modal.Title className="text-lg font-bold">ثبت بازرسی #{selectedTask?.id}</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto space-y-6">
//                     <GPSLocation
//                         onLocation={(lat, lng) => { setInspectorLat(lat); setInspectorLng(lng); }}
//                         lat={inspectorLat}
//                         lng={inspectorLng}
//                     />
//                     <div>
//                         <label className="block mb-1 text-sm font-medium">تعداد شمارش شده بازرس</label>
//                         <input
//                             type="text"
//                             inputMode="numeric"
//                             value={inspectorCount}
//                             onChange={(e) => setInspectorCount(e.target.value)}
//                             placeholder="تعداد را وارد کنید"
//                             className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700"
//                         />
//                     </div>
//                     <div>
//                         <CameraCapture
//                             maxCount={5}
//                             onChange={setInspectorImages}
//                             currentFiles={inspectorImages}
//                             buttonLabel="تصاویر بازرس"
//                             buttonClassName="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg"
//                         />
//                     </div>
//                     <div className="p-3 bg-yellow-50 text-yellow-800 rounded-lg text-xs dark:bg-yellow-900/20 dark:text-yellow-300">
//                         <strong>توجه:</strong> موقعیت مکانی به‌صورت خودکار از GPS دستگاه دریافت می‌شود. در صورت بروز خطا، دوباره تلاش کنید.
//                     </div>
//                 </Modal.Body>
//                 <Modal.Footer className="flex justify-end gap-3 p-5 border-t">
//                     <button onClick={closeSubmitModal} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">انصراف</button>
//                     <button onClick={handleSubmitTask} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
//                         {submitting ? "در حال ثبت..." : "ثبت بازرسی"}
//                     </button>
//                 </Modal.Footer>
//             </Modal>

//             {lightboxOpen && (
//                 <ImageLightbox
//                     images={galleryImages}
//                     initialIndex={lightboxIndex}
//                     onClose={() => setLightboxOpen(false)}
//                 />
//             )}
//         </React.Fragment>
//     );
// };

// export default InspectorTasks;

